import { CloudUpload, DeleteOutline } from "@mui/icons-material";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import React, { useEffect, useMemo, useRef, useState } from 'react'
import c from "lib/style/colorVar.scss";

const DEFAULT_CONFIG = {
  allowedMimeTypes: ["image/jpeg", "image/png"],
  maxSize: 2 * 1024 * 1024, // 2MB
  dimension: null
}

const styles = {
  mandatoryAsterisk: {
    color: "red",
  },
  descriptionText: {
    color: "grey",
  },
  hintText: {
    color: "red",
  },
  deleteButton: {
    backgroundColor: c.brand1,
    "&:hover": {
      backgroundColor: c.brandLight92,
    },
  },
  container: {
    marginBottom: "18px",
  },
  uploadBox: {
    width: 150,
    height: 150,
    border: "2px dashed",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",

    borderColor: "divider",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "primary.main",
    },
  },
  uploadBoxError: {
    borderColor: "error.main",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "error.main",
    },
  },
  uploadBoxDisabled: {
    cursor: "not-allowed",
    backgroundColor: "action.hover",
    "&:hover": {
      borderColor: "divider !important",
    },
  },
  uploadBoxReadonly: {
    cursor: "auto",
    "&:hover": {
      backgroundColor: "unset",
      borderColor: "divider !important",
    },
  },
  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: 2,
  },
  uploadText: {
    mt: 1,
    color: "text.secondary",
  },
  uploadTextError: {
    mt: 1,
    color: "error.main",
  },
  helperMessageContainer: {
    flexDirection: "row",
  },
};

/**
 * @typedef {Object} dimensionConfig
 * @property {[number, number]} widthLimit - [minimum, maximum] leave empty for no limit
 * @property {[number, number]} heightLimit - [minimum, maximum] leave empty for no limit
 */

/**
 * @typedef {Object} imagePickerConfig
 * @property {("image/jpeg" | "image/png")[]} allowedMimeTypes
 * @property {number} maxSize
 * @property {dimensionConfig?} dimension
 */

/**
 * @param {Object} props 
 * @param {React.Ref<HTMLInputElement>?} props.inputRef
 * @param {string?} props.currentImage - base64String value of current image
 * @param {string?} props.defaultImage - base64String value of default image
 * @param {(base64String: string) => void} props.onChange 
 * @param {(base64String: string) => void} props.onInit - first parameter is the base64 string of the default image
 * @param {(errorMessage: string) => void} props.onError 
 * @param {boolean} props.disabled 
 * @param {boolean} props.readOnly
 * @param {string?} props.errorMessage
 * @param {boolean?} props.noErrorMessage
 * @param {imagePickerConfig} props.config
 */
const ImagePicker = ({
  inputRef,
  currentImage,
  defaultImage,
  onChange,
  onError,
  disabled,
  readOnly,
  errorMessage,
  noErrorMessage = false,
  config,
}) => {
  const [image, setImage] = useState(null);
  const inputElementRef = inputRef || useRef();

  const parsedConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config,
  }), [config]);

  const handleFileChange = (e) => {
    /** @type {File} */
    const file = e.target.files?.[0];

    const readFile = () => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
        const signature = reader?.result?.split(",")[1];
        let isValid = false;
        parsedConfig.allowedMimeTypes.forEach((type) => {
          switch (type) {
            case "image/jpeg":
              if (signature?.startsWith("/9j/")) {
                isValid = true
              }
              break;
            case "image/png":
              if (signature?.startsWith("iVBORw0KGgo")) {
                isValid = true
              }
              break;
          }
        })
        if (isValid) {
          onChange(base64Image);
          setImage(base64Image);
        } else {
          onError("You can only upload JPG/PNG file!");
        }
      }

      reader.readAsDataURL(file);
    }

    if (!file) {
      return;
    }

    if (!parsedConfig.allowedMimeTypes.includes(file.type)) {
      onError("You can only upload JPG/PNG file!");
      return;
    }

    if (file.size > parsedConfig.maxSize) {
      onError(`Image size must be less than ${parsedConfig.maxSize / 1024 / 1024} MB!`);
      return;
    }

    if (!parsedConfig.dimension) {
      readFile();
    } else {
      const img = new Image();

      img.onload = () => {
        const { width, height } = img;
        const { widthLimit, heightLimit } = parsedConfig.dimension;

        const isWidthInvalid = widthLimit && (width < widthLimit[0] || width > widthLimit[1]);
        const isHeightInvalid = heightLimit && (height < heightLimit[0] || height > heightLimit[1]);
        if (isWidthInvalid || isHeightInvalid) {
          onError(`Image dimensions must be between ${widthLimit[0]}x${heightLimit[0]}px and ${widthLimit[1]}x${heightLimit[1]}px`)
          URL.revokeObjectURL(img.src);
          return
        }
        URL.revokeObjectURL(img.src);
        readFile();
      };

      img.src = URL.createObjectURL(file);
    }
  }

  useEffect(() => {
    if (defaultImage) {
      setImage(defaultImage);
      onChange(currentImage);
    }
  }, [defaultImage]);

  useEffect(() => {
    if (currentImage !== image) {
      setImage(currentImage);
      onChange(currentImage);
    }
  }, [currentImage]);

  return (
    <Box sx={{ textAlign: "center", position: "relative" }}>
      <Stack gap={2} direction={"row"} alignItems={"center"}>
        <Box
          onClick={() => inputElementRef.current.click()}
          sx={{ position: "relative" }}>
          <input
            ref={inputElementRef}
            type="file"
            accept={parsedConfig.allowedMimeTypes?.join(", ")}
            value={""} //force uncontrolled to prevent bug
            onChange={handleFileChange}
            disabled={disabled || readOnly}
            style={{ display: "none" }}
          />
          <Box sx={{
            ...styles.uploadBox,
            ...errorMessage && styles.uploadBoxError,
            ...disabled && styles.uploadBoxDisabled,
            ...readOnly && styles.uploadBoxReadonly
          }}>
            {image ? (
              <Avatar
                key={image}
                src={image}
                variant="square"
                sx={styles.avatar}
              />
            ) : (
              <>
                <CloudUpload
                  fontSize="large"
                  color={errorMessage ? "error" : "action"}
                />
                <Typography
                  variant="body2"
                  sx={
                    errorMessage ? styles.uploadTextError : styles.uploadText
                  }
                >
                  Click to upload
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {image && !disabled && !readOnly && (
          // Delete button
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              onChange(null);
              setImage(null);
            }}
            sx={styles.deleteButton}
            disabled={disabled || readOnly}
          >
            <DeleteOutline sx={{ color: "white" }} fontSize="medium" />
          </IconButton>
        )}
      </Stack>
      {!noErrorMessage && <Stack sx={styles.helperMessageContainer}>
        {errorMessage && <HelperMessage message={errorMessage} />}
      </Stack>}
    </Box>
  )
}

export default ImagePicker